import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getCardsBreakeven,
  getCardsOptimization,
  getCardsRecommendations,
} from "../lib/api-modules/cards.js";
import {
  Card,
  EmptyState,
  Pill,
  SectionHeader,
  SelectField,
  StatCard,
  Tab,
  Tabs,
  TextField,
} from "../components/design-system/index.js";

import { SuggestedPromptsTile } from "../components/SuggestedPromptsTile.js";
import { PAGE_PROMPTS } from "../lib/data/page-prompts.js";
/**
 * /cards — Phase 8 Session 3.
 *
 * Three tabs:
 *   1. Optimization: per-account credit-card rewards-vs-fee analysis
 *      against the household's last-90d spending mix.
 *   2. Recommendations: "best card for [category]" against the curated
 *      reward table (no household data needed).
 *   3. Breakeven: per-card monthly spend needed to recoup the AF.
 *
 * Reward-table snapshot date + stale warning surface in the header.
 */

const CATEGORY_OPTIONS = [
  "default",
  "dining",
  "groceries",
  "gas",
  "travel",
  "travel_portal",
  "streaming",
  "transit",
  "drugstore",
  "online_grocery",
  "amazon",
  "entertainment",
] as const;

type ActiveTab = "optimization" | "recommendations" | "breakeven";

export function CardsPage() {
  const [activeTab, setActiveTab] = useState<ActiveTab>("optimization");
  return (
    <div className="mx-auto max-w-5xl p-6">
      <SectionHeader
        title="Cards"
        sub="Credit-card rewards optimization. Hearth fuzzy-matches each connected card account against a curated reward table, then projects net annualized rewards using your last-90d spending mix."
      />
      <Tabs
        value={activeTab}
        onChange={(v) => setActiveTab(v as ActiveTab)}
        ariaLabel="Cards optimization tabs"
      >
        <Tab value="optimization" label="Your cards" />
        <Tab value="recommendations" label="Best by category" />
        <Tab value="breakeven" label="AF breakeven" />
      </Tabs>

      <div className="mt-6">
        {activeTab === "optimization" && <OptimizationTab />}
        {activeTab === "recommendations" && <RecommendationsTab />}
        {activeTab === "breakeven" && <BreakevenTab />}
      </div>
      <div className="mt-8">
        <SuggestedPromptsTile prompts={PAGE_PROMPTS.cards} />
      </div>
    </div>
  );
}

function OptimizationTab() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["cards-optimization"],
    queryFn: getCardsOptimization,
  });
  if (isLoading) return <p className="text-sm">Loading optimization…</p>;
  if (error) return <Err message={error.message} />;
  if (!data) return null;
  return (
    <div className="space-y-4">
      <SnapshotBanner asOf={data.as_of} isStale={data.is_stale} />
      {data.cards.length === 0 && data.unmatched_accounts.length === 0 ? (
        <EmptyState
          title="No credit-card accounts yet"
          description="Add a credit-card account or connect one via Plaid to see per-card rewards-vs-fee analysis. Hearth never auto-merges synced rows — they land in your staging inbox first."
        />
      ) : null}
      <div className="grid gap-4 md:grid-cols-2">
        {data.cards.map((c) => (
          <Card key={c.account_id}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <h3 className="text-base font-semibold">{c.account_name}</h3>
                <p className="text-xs text-[var(--color-muted)]">{c.institution}</p>
                <p className="mt-1 text-xs text-[var(--color-muted)]">
                  Matched: <Pill tone="neutral">{c.card_match.name}</Pill>{" "}
                  AF ${(c.card_match.annual_fee_cents / 100).toFixed(0)}
                </p>
              </div>
              <div className="text-right">
                <div className="text-xs text-[var(--color-muted)]">
                  Net annual reward
                </div>
                <div
                  className={`text-xl font-semibold ${c.net_annual_cents >= 0 ? "text-[var(--color-success)]" : "text-[var(--color-danger)]"}`}
                >
                  ${(c.net_annual_cents / 100).toFixed(0)}
                </div>
                {c.breakeven_monthly_cents !== null &&
                Number.isFinite(c.breakeven_monthly_cents) ? (
                  <div className="text-xs text-[var(--color-muted)]">
                    Breakeven $
                    {((c.breakeven_monthly_cents ?? 0) / 100).toFixed(0)}
                    /mo on {c.breakeven_bonus_category}
                  </div>
                ) : (
                  <div className="text-xs text-[var(--color-muted)]">No AF</div>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {data.unmatched_accounts.length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold">Unmatched cards</h3>
          <p className="mt-1 text-xs text-[var(--color-muted)]">
            These credit-card accounts didn't fuzzy-match the reward
            table. v1 doesn't support manual mapping yet (Phase 11) —
            rename the account to include the card name (e.g.,
            "Sapphire Preferred — primary") to match.
          </p>
          <ul className="mt-2 space-y-1 text-sm">
            {data.unmatched_accounts.map((a) => (
              <li key={a.account_id}>
                {a.account_name} <span className="text-[var(--color-muted)]">— {a.institution}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function RecommendationsTab() {
  const [category, setCategory] = useState<string>("dining");
  const [monthlySpendDollars, setMonthlySpendDollars] = useState<string>("500");
  const monthlySpendCents = useMemo(() => {
    const n = Number.parseFloat(monthlySpendDollars);
    return Number.isFinite(n) ? Math.round(n * 100) : 0;
  }, [monthlySpendDollars]);

  const { data, error, isLoading } = useQuery({
    queryKey: ["cards-recommendations", category, monthlySpendCents],
    queryFn: () =>
      getCardsRecommendations({
        category,
        monthly_spend_cents: monthlySpendCents,
      }),
  });

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-wrap gap-3">
          <SelectField
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </SelectField>
          <TextField
            label="Monthly spend ($)"
            value={monthlySpendDollars}
            onChange={(e) => setMonthlySpendDollars(e.target.value)}
            type="number"
            min={0}
          />
        </div>
      </Card>
      {isLoading && <p className="text-sm">Ranking cards…</p>}
      {error && <Err message={error.message} />}
      {data && (
        <>
          <SnapshotBanner asOf={data.as_of} isStale={data.is_stale} />
          <div className="space-y-2">
            {data.ranked.map((r, i) => (
              <Card key={r.card_id}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-sm">
                      <span className="font-mono text-[var(--color-muted)]">
                        #{i + 1}
                      </span>{" "}
                      <span className="font-semibold">{r.name}</span>{" "}
                      <span className="text-[var(--color-muted)]">— {r.issuer}</span>
                    </div>
                    <p className="mt-1 text-xs text-[var(--color-muted)]">
                      {r.rationale}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-[var(--color-muted)]">
                      Net / yr
                    </div>
                    <div className="text-lg font-semibold">
                      ${(r.net_annual_cents / 100).toFixed(0)}
                    </div>
                    <div className="text-xs text-[var(--color-muted)]">
                      ({(r.effective_rate_bp / 100).toFixed(2)}% effective)
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function BreakevenTab() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["cards-breakeven"],
    queryFn: getCardsBreakeven,
  });
  if (isLoading) return <p className="text-sm">Loading breakeven…</p>;
  if (error) return <Err message={error.message} />;
  if (!data) return null;
  return (
    <div className="space-y-3">
      <SnapshotBanner asOf={data.as_of} isStale={data.is_stale} />
      <div className="grid gap-3 md:grid-cols-2">
        {data.rows.map((r) => (
          <StatCard
            key={r.card_id}
            label={`${r.name} (${r.issuer})`}
            value={
              r.annual_fee_cents === 0
                ? "No AF"
                : r.monthly_breakeven_cents === null ||
                    !Number.isFinite(r.monthly_breakeven_cents)
                  ? "AF not recoverable"
                  : `$${((r.monthly_breakeven_cents ?? 0) / 100).toFixed(0)}/mo`
            }
            sub={
              r.annual_fee_cents === 0
                ? undefined
                : `AF $${(r.annual_fee_cents / 100).toFixed(0)}${r.bonus_category ? ` · best on ${r.bonus_category}` : ""}`
            }
          />
        ))}
      </div>
    </div>
  );
}

function SnapshotBanner({ asOf, isStale }: { asOf: string; isStale: boolean }) {
  return (
    <p className="text-xs text-[var(--color-muted)]">
      Reward table snapshot as of {asOf}.{" "}
      {isStale ? (
        <span className="text-[var(--color-danger)]">
          Snapshot more than 6 months old — verify rates before acting.
        </span>
      ) : null}
    </p>
  );
}

function Err({ message }: { message: string }) {
  return (
    <p className="text-sm text-red-700 dark:text-red-300">Error: {message}</p>
  );
}
